import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';
import Input from './ui/Input';
import Select from './ui/Select';
import Textarea from './ui/Textarea';
import FormField from './ui/FormField';
import FormRow from './ui/FormRow';
import Button from './ui/button';
import CitySearchInput from '../features/geocoding/CitySearchInput';

type VisitChoice = 'YES' | 'MAYBE' | 'NO';

interface FriendFormData {
  name: string;
  email: string;
  city: string;
  country: string;
  latitude?: number;
  longitude?: number;
  comment: string;
  visitOnNextJourney: VisitChoice | '';
}

interface Friend {
  id: string;
  name: string;
  email?: string;
  city: string;
  country: string;
  latitude?: number;
  longitude?: number;
  comment?: string;
  visitOnNextJourney: VisitChoice;
  createdAt: string;
  updatedAt: string;
}

interface FriendFormProps {
  friend?: Friend | null;
  onSave: (friendData: Omit<Friend, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

const FriendForm: React.FC<FriendFormProps> = ({ friend, onSave, onCancel }) => {
  const [formData, setFormData] = useState<FriendFormData>({
    name: '',
    email: '',
    city: '',
    country: '',
    latitude: undefined,
    longitude: undefined,
    comment: '',
    visitOnNextJourney: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FriendFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (friend) {
      setFormData({
        name: friend.name,
        email: friend.email || '',
        city: friend.city,
        country: friend.country,
        latitude: friend.latitude,
        longitude: friend.longitude,
        comment: friend.comment || '',
        visitOnNextJourney: friend.visitOnNextJourney,
      });
    }
  }, [friend]);

  // Lock body scroll while the modal is open (matches LocationDialog/FormDialog).
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FriendFormData]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FriendFormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    else if (formData.name.trim().length < 2) newErrors.name = 'Name must be at least 2 characters';
    if (!formData.city.trim() || formData.city.trim().length < 3) newErrors.city = 'Search and pick a city from the list';
    if (!formData.country.trim()) newErrors.country = 'Country is auto-filled. Please pick a city above';
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.visitOnNextJourney) newErrors.visitOnNextJourney = 'Please choose an option';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      // Uniform UX with the rest of the app: when the city autocomplete is
      // the (or a) problem, surface its specific message in a toast — the
      // red border is driven by `invalid={!!errors.city}` below.
      const cityProblem = !formData.city.trim() || !formData.country.trim();
      if (cityProblem) {
        showToast({
          title: 'City selection required',
          message: 'Pick a city from the suggestions.',
          type: 'error',
          duration: 5000,
        });
      } else {
        showToast({ title: 'Validation Error', message: 'Please fix the errors in the form.', type: 'error', duration: 4000 });
      }
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave({
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        city: formData.city.trim(),
        country: formData.country.trim(),
        latitude: formData.latitude,
        longitude: formData.longitude,
        comment: formData.comment.trim() || undefined,
        visitOnNextJourney: formData.visitOnNextJourney as VisitChoice,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        zIndex: 2002,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="friend-form-title"
        style={{
          backgroundColor: 'var(--surface)',
          borderRadius: 'var(--radius-md)',
          padding: 'clamp(1rem, 4vw, 1.5rem)',
          width: 'min(92vw, 500px)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 id="friend-form-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {friend ? 'Edit Friend' : 'Add New Friend'}
          </h2>
          <Button
            type="button"
            variant="ghost"
            aria-label="Close"
            onClick={onCancel}
            style={{ width: 34, height: 34, padding: 0, fontSize: '1.25rem', lineHeight: 1 }}
          >
            ×
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <FormField label="Name *" error={errors.name}>
            <Input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter friend's name" />
          </FormField>

          <FormRow columns={1}>
            <FormField label="City *">
              <CitySearchInput
                initialLabel={formData.city}
                placeholder="Search for a city"
                invalid={!!errors.city}
                onSelect={(city) => {
                  setFormData(prev => ({
                    ...prev,
                    city: city ? city.name : '',
                    country: city ? (city.country ?? '') : '',
                    latitude: city ? city.lat : undefined,
                    longitude: city ? city.lng : undefined,
                  }));
                  if (city) {
                    setErrors(prev => ({ ...prev, city: undefined, country: undefined }));
                  }
                }}
              />
            </FormField>
            <FormField
              label="Country"
              hint="(auto-filled from city)"
            >
              <Input
                type="text"
                name="country"
                value={formData.country}
                readOnly
                tabIndex={-1}
                placeholder="Pick a city above"
                style={{ backgroundColor: 'var(--surface-alt)', color: 'var(--text-muted)', cursor: 'not-allowed' }}
              />
            </FormField>
          </FormRow>

          <FormField label="Visit on next journey *" error={errors.visitOnNextJourney}>
            <Select name="visitOnNextJourney" value={formData.visitOnNextJourney} onChange={handleChange}>
              <option value="" disabled>Select an option…</option>
              <option value="YES">Yes</option>
              <option value="MAYBE">Maybe</option>
              <option value="NO">No</option>
            </Select>
          </FormField>

          <FormField label="Comment">
            <Textarea name="comment" value={formData.comment} onChange={handleChange} rows={2} placeholder="Add a comment about this friend (optional)" />
          </FormField>

          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
            <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : (friend ? 'Update Friend' : 'Add Friend')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FriendForm;
